import { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useForm, useWatch } from "react-hook-form";
import { formFieldsTable as formFields, getConfigFormModalSelect } from "./form-config";
import { useNavigate } from "react-router";
import { PATHS } from "../../../constants";
import { useAction } from "../../../hooks/actions";
import ObjectDialog from "../ObjectsList/ObjectDialog";
import { EmployeeDialog } from "./EmployeeDialog";
import { getColumns } from "./columns";
import { useAppSelector } from "../../../hooks/redux";
import EditButton from "../../components/EditButton";
import { useLazyGetAllEmployeesQuery } from "../../../middleware/employeeApi";
import TableBasic from "../../components/TableBasic";
import { ModalSelectColumnsServer } from "../../components/Modals/ModalSelectColumnsServer";
import { useGetUserGridSettingQuery, useLazyGetUserGridSettingQuery } from "../../../middleware/usersApi";
import { TooltipProps } from "../../../helpers/materialUITheme";
import ClearFilterIcon from "../../components/icons/ButtonIcons/ClearFilterIcon";
import SettingsIcon from "../../components/icons/ButtonIcons/SettingsIcon";
import PlusIcon from "../../components/icons/ButtonIcons/PlusIcon";
import { getAddressString, getTableHieght, removeEmptyFields } from "../../../helpers/tableHelper";
import useScreenSize from "../../../hooks/useScreenSize";
import "./styles/style.css";

export const EmployeeList = () => {
  const screenSize = useScreenSize();
  const { t } = useTranslation();
  const {
    searchemployeeFullNameStorage,
    searchemployeeLoginStorage,
    searchphoneNumberStorage,
    searchSubdivisionNameStorage,
    searchSubdivisionAddressStorage,
    pageNumberStorage,
  } = JSON.parse(sessionStorage.getItem("employeeListSearchParams")) || {};
  const { control, reset } = useForm({
    mode: "onChange",
    defaultValues: {
      [formFields.searchParams]: {
        [formFields.employeeLogin]: searchemployeeLoginStorage || "",
        [formFields.employeeFullName]: searchemployeeFullNameStorage || "",
        [formFields.employeePhoneNumber]: searchphoneNumberStorage || "",
        [formFields.subdivisionName]: searchSubdivisionNameStorage || "",
        [formFields.subdivisionAddress]: searchSubdivisionAddressStorage || "",
      },
    },
  });
  const navigate = useNavigate();
  const params = useWatch({
    control,
    name: formFields.searchParams,
  });

  const { allEmployees } = useAppSelector((state) => state.employee);
  const { setIsErrorRequest } = useAction();
  const [openObjectDialog, setOpenObjectDialog] = useState(false);
  const [selectedObjectId, setSelectedObjectId] = useState(null);
  const [pageNumber, changePageNumber] = useState(pageNumberStorage || 0);
  const [openSelectModal, setOpenSelectModal] = useState(false);
  const [openEmployeeDialog, setOpenEmployeeDialog] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

  const { data: gridColumns } = useGetUserGridSettingQuery({ gridSettingName: "EmployeeList" });

  const [getUserGrid] = useLazyGetUserGridSettingQuery();
  const [getAllEmployees, { isLoading, error }] = useLazyGetAllEmployeesQuery();

  const handleOpenEmployeeDialogState = (id) => {
    if (!openEmployeeDialog) {
      setOpenEmployeeDialog(true);
      setSelectedEmployeeId(id);
      return;
    }
    setOpenEmployeeDialog(false);
    setSelectedEmployeeId(null);
  };

  const handleOpenObjectDialogState = (id) => {
    if (!openObjectDialog) {
      setOpenObjectDialog(true);
      setSelectedObjectId(id);
      return;
    }
    setOpenObjectDialog(false);
    setSelectedObjectId(null);
  };

  const handleLink = (id) => navigate(PATHS.GET_EDIT_EMPLOYEE(id));

  const rows = useMemo(() => {
    const { data } = allEmployees || {};
    return data?.length
      ? data.map(
          ({
            employeeId,
            avatarLink,
            enableForEdit,
            employeeFullName,
            subdivisionName,
            subdivisionAddresses,
            employeeLogin,
            phoneNumber,
          }) => ({
            employeeId,
            avatarLink,
            phoneNumber: {
              link: phoneNumber,
              type: "tel:",
            },
            employeeFullName: {
              name: employeeFullName,
              action: () => employeeId && handleOpenEmployeeDialogState(employeeId),
            },
            subdivisionName,
            subdivisionAddress: subdivisionAddresses,
            employeeLogin,
            edit: enableForEdit ? <EditButton handleClick={() => handleLink(employeeId)} /> : <></>,
          })
        )
      : [];
  }, [allEmployees]);

  const columnsFromStorage = useMemo(() => {
    const storageData = gridColumns?.columnSettings || [];
    return storageData?.reduce((acc, { name, visible }) => ({ ...acc, [name]: visible }), {});
  }, [gridColumns]);

  const columns = useMemo(
    () =>
      getColumns({
        control,
        columnsFromStorage,
        writeSearchDataToSessionStorage,
        rows,
      }),

    [columnsFromStorage]
  );

  const recordsCount = useMemo(() => allEmployees && allEmployees?.recordsCount, [allEmployees]);

  const handleOpenSelectColumnsModal = () => {
    setOpenSelectModal(true);
  };

  useEffect(() => {
    const address = getAddressString(params?.subdivisionAddress, "SubdivisionAddressFilter");
    delete params?.subdivisionAddress;
    const preparedParams = removeEmptyFields(params);

    getAllEmployees({
      ...preparedParams,
      address,
      pageNumber: 0,
    });
  }, [params]);

  useEffect(() => {
    const address = getAddressString(params?.subdivisionAddress, "SubdivisionAddressFilter");
    delete params?.subdivisionAddress;
    const preparedParams = removeEmptyFields(params);

    getAllEmployees({
      ...preparedParams,
      address,
      pageNumber: pageNumber,
    });
  }, [pageNumber]);

  useEffect(() => {
    setIsErrorRequest(error);
  }, [error]);

  function writeSearchDataToSessionStorage(newSearchParam, searchValue) {
    const getStorage = JSON.parse(sessionStorage.getItem("employeeListSearchParams")) || {};
    const searchParamsForLocalStorage = {
      ...getStorage,
      [newSearchParam]: searchValue,
    };
    sessionStorage.setItem("employeeListSearchParams", JSON.stringify(searchParamsForLocalStorage));
  }
  function clearFiltersHandler() {
    changePageNumber(0);

    sessionStorage.removeItem("employeeListSearchParams");
    reset({});
  }

  return (
    <Box className="template_page magazine_events">
      {openObjectDialog && (
        <ObjectDialog open={openObjectDialog} onClose={handleOpenObjectDialogState} objectId={selectedObjectId} />
      )}

      {openEmployeeDialog && (
        <EmployeeDialog
          open={openEmployeeDialog}
          onClose={handleOpenEmployeeDialogState}
          employeeId={selectedEmployeeId}
        />
      )}

      <Box className="template_page-header">
        <Typography className="title-page">{t("title_page:employees")}</Typography>
        <Box sx={{ display: "flex", gap: "8px" }}>
          <Tooltip title="Очистить все фильтры" componentsProps={TooltipProps} placement="bottom" arrow>
            <Button className="btn btn-white" onClick={clearFiltersHandler}>
              <ClearFilterIcon />
            </Button>
          </Tooltip>

          <Tooltip
            title={t("button:customize_fields_to_display")}
            componentsProps={TooltipProps}
            placement="bottom"
            arrow
          >
            <Button className="btn btn-white" onClick={handleOpenSelectColumnsModal}>
              <SettingsIcon />
            </Button>
          </Tooltip>

          <Button
            className="btn btn-primary-fill"
            variant="outlined"
            onClick={() => {
              navigate(PATHS.ADD_EMPLOYEE);
            }}
          >
            <PlusIcon />
            {t("button:add")}
          </Button>
        </Box>
      </Box>

      <form>
        <TableBasic
          columns={columns}
          rows={rows}
          handleClickCell={() => {}}
          withSearchField={false}
          isLoading={isLoading}
          pageNumber={pageNumber}
          changePageNumber={changePageNumber}
          count={recordsCount}
          minHeight={getTableHieght(screenSize, false, "320px").min}
          maxHeight={getTableHieght(screenSize, false, "320px").max}
        />
      </form>

      <ModalSelectColumnsServer
        open={openSelectModal}
        setOpen={setOpenSelectModal}
        data={columnsFromStorage}
        refetch={() => getUserGrid({ gridSettingName: "EmployeeList" })}
        nameStorage={"EmployeeList"}
        config={getConfigFormModalSelect(gridColumns?.columnSettings || [])}
      />
    </Box>
  );
};
